import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import APIServices from '../../api/APIServices'

// Create a unique, descriptive key for this query
const BUSINESS_REGIONS_QUERY_KEY = 'business-regions'

export interface BusinessRegion {
  id: number
  name: string
  description: string | null
  price: {
    amount: number
    codeCurrency: string
  }
  priceSystemId?: number
}

export interface UseBusinessRegionsQueryParams {
  businessId: number
  priceSystemId?: number
  municipalityId?: number
}

type BusinessRegionsResponse = BusinessRegion[]

/**
 * Fetch business regions from the API
 * Separating API call logic from the hook for better testability
 */
const fetchBusinessRegions = async ({
  municipalityId,
}: {
  municipalityId?: number
}): Promise<BusinessRegionsResponse> => {
  const response = await APIServices.get('/invoice/regions/all', {
    params: { municipalityId },
  })
  console.log('regions response', response)
  return response.data
}

/**
 * Custom hook for fetching business delivery regions
 *
 * @param params - Query parameters including optional priceSystemId filter
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useBusinessRegionsQuery = ({
  params,
  options,
}: {
  params: UseBusinessRegionsQueryParams
  options?: Partial<UseQueryOptions<BusinessRegionsResponse, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<BusinessRegionsResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [BUSINESS_REGIONS_QUERY_KEY, params],

    // Separate the API call logic from the query configuration
    queryFn: () =>
      fetchBusinessRegions({ municipalityId: params?.municipalityId }),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  return {
    regions: queryResult.data || [],
    isLoadingRegions: queryResult.isLoading,
    isErrorRegions: queryResult.isError,
    errorRegions: queryResult.error,
    refetchRegions: queryResult.refetch,
    isFetchingRegions: queryResult.isFetching,
  }
}

/**
 * Helper function to get the business regions query key
 * Useful for cache invalidation or prefetching
 */
export const getBusinessRegionsQueryKey = (
  params: UseBusinessRegionsQueryParams
) => [BUSINESS_REGIONS_QUERY_KEY, params]
